import { Component } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { TRAILER_TYPES } from '../work-orders/select-options';
import { FormControl, FormBuilder, FormGroup, Validators, ValidatorFn, AbstractControl } from '@angular/forms';
import { faArrowLeft, faMoon, faSun, faFile } from '@fortawesome/free-solid-svg-icons';
import { Router } from '@angular/router';

@Component({
  selector: 'app-full-page-order-maker',
  templateUrl: './full-page-order-maker.component.html',
  styleUrls: ['./full-page-order-maker.component.scss']
})
export class FullPageOrderMakerComponent {
  goBackButton = faArrowLeft;
  darkModeButton = faMoon;
  lightModeButton = faSun;
  submitButton = faFile;

  // error messages
  invalidInputs: Array<string> = ['load length: must be a number', 'destination: not a valid address'];
  requiredInputs: Array<string> = ['shipper', 'load weight', 'customer'];

  // drop down options
  EQUIPMENT_OPTIONS: Array<string> = ['', 'Tarps', 'Hazardous', 'Pallet Exchange', 'Team', 'Tilt Load Lift Equipment', 'Expedited', 'Bonded', 'Power Tailgate', 'Spot/Drop Trailer', 'Mult-Stop', 'Blanket Wrap', 'Temperature Control', 'Air Ride', 'Satellite Tracking', 'Heated', 'Lumper/Swamper', 'Tradeshow'];
  EQUIPMENT_TYPES = [''].concat(Object.values(TRAILER_TYPES)) ;
  ORDER_SCOPE: Array<string> = ['', 'Tradeshow', 'Storage', 'Trailer Build', 'Warehouse', 'Household', 'Plug Play', 'Blanketed', 'Dock to Dock', 'White Glove', 'Military Site', 'Extraction', 'Disposal', 'Job Site'];
  CONTACT_TYPE = {
    'shipper': 'shipper_contact_list',
    'consignee': 'consignee_contact_list',
    'customer': 'customer_contact_list',
    'carrier': 'carrier_contact_list',
    'broker': 'broker_contact_list',
    'insurance': 'insurance_contact_list'
  }

  // pallets
  pallet_form = this.fb.group({
    pallet_or_product: ['', Validators.required],
    pallet_qty: [''],
    model_num: [''],
    length: ['', Validators.required],
    width: ['', Validators.required],
    height: ['', Validators.required],
    weight: [0, Validators.required],
    description: ['']
  }, { validators: PalletInfoValidator});
  pallet_set: FormGroup[] = [];

  shipper_form = this.fb.group({
    shipper_loading_dock: [null],
    shipper_pickup_tailgate: [null],
    shipper_truck_space: [null],
    shipper_pickup_straight_truck: [null],
    shipper_bol_required: [null],
    shipper_is_residential: [null],
    shipper_is_commercial: [null],
    shipper_is_job_site: [null],
    shipper_is_convention_center: [null],
    shipper_is_pop_up: [null],
    appointment_shipper: [null],
    shipper_contact_uuid: ['', Validators.required],
    shipper_address_uuid: ['', Validators.required],
    shipper_company_name: ['', [Validators.required, CompanyNameValidator]], // do not add to post data
    shipper_company_address: ['', Validators.required] // do not add to post data
  })

  transportations_requirements_form = this.fb.group({
    customer_csa: [null],
    customer_fast: [null],
    customer_ace: [null],
    customer_aci: [null],
    customer_ct_pat: [null],
    customer_canada_bond: [null],
    customer_us_bond: [null],
    customer_hazmat: [null],
    customer_protect_from_freezing: [null],
    customer_heated: [null],
    customer_refrigeration: [null],
    customer_food_grade: [null]
  })

  customer_form = this.fb.group({
    customer_at_pickup_notification: [null],
    customer_delayed_pickup_notification: [null],
    customer_at_delivery_notification: [null],
    customer_delayed_delivery_notification: [null],
    customer_assessorials_incurred_notification: [null],
    customer_shipping_bol: [null],
    customer_invoice_bol: [null],
    customer_invoice_pod_required: [null],
    customer_invoice_po_required: [null],
    customer_invoice_po: [''],
    customer_rate_quote: [null],
    customer_contact_uuid: ['', Validators.required],
    customer_address_uuid: ['', Validators.required],
    customer_company_name: ['', [Validators.required, CompanyNameValidator]], // do not add to post data
    customer_company_address: ['', Validators.required] // do not add to post data
  })

  consignee_form = this.fb.group({
    consignee_loading_dock: [null],
    consignee_pickup_tailgate: [null],
    consignee_truck_space: [null],
    consignee_pickup_straight_truck: [null],
    consignee_bol_required: [null],
    consignee_is_commercial: [null],
    consignee_is_residential: [null],
    consignee_is_job_site: [null],
    consignee_is_convention_center: [null],
    consignee_is_pop_up: [null],
    appointment_consignee: [null],
    consignee_contact_uuid: ['', Validators.required],
    consignee_address_uuid: ['', Validators.required],
    consignee_company_name: ['', [Validators.required, CompanyNameValidator]], // do not add to post data
    consignee_company_address: ['', Validators.required] // do not add to post data
  })

  bill_to_form = this.fb.group({
    bill_to_contact_uuid: [''],
    bill_to_address_uuid: [''],
    bill_to_company_name: ['', CompanyNameValidator],
    bill_to_company_address: ['']
  })

  ops_form = this.fb.group({
    carrier_rated: [null],
    carrier_rerated: [null],
    straps_bars: [null],
    protective_equipment: [null]
  })

  // these will need to be put into an array before sending to post data as value for property packaging_types
  packaging_types_form = this.fb.group({
    palleted: [null],
    carton: [null],
    crate: [null],
    loose_floor: [null]
  })

  // these need to be put into array before sending to post as value for property lading_type
  order_type_form = this.fb.group({
    usa_domestic: [null],
    canada_domestic: [null],
    usa_crossborder: [null],
    canada_crossborder: [null],
    air_freight: [null],
    ocean_freight: [null],
    local_delivery: [null]
  })

  load_form = this.fb.group({
    partial_or_full: ['', Validators.required],
    type_of_equipment: ['', Validators.required],
    order_scope: [''],
    order_scope_secondary: [''],
    product_status: [''],
    lading_number: [''],
    booking_number: [''],
    requirements: [''],
    trip_number: [''],
    release_number: [''],
    load_details: [''],
    load_special_info: [''],
    total_length: [''],
    load_value_unit: [''],
    load_value: [''],
    shipment_number: [''],
    equipment_options: ['']
  })

  pickup_delivery_form = this.fb.group({
    appointment_date_shipper: [''],
    appointment_time_start_shipper: [''],
    appointment_date_consignee: [''],
    appointment_time_start_consignee: [''],
    shipper_pickup_reference: [''],
    consignee_delivery_reference: ['']
  })

  operations_form = this.fb.group({
    operations_notation: ['']
  })

  constructor(private fb: FormBuilder, private http: HttpClient, private router: Router){
    this.pallet_set.push(this.pallet_form)
  }

  goBack(){
    this.router.navigate(['/']);
    console.log('hello')
  }

  autofillContact(type: keyof typeof this.CONTACT_TYPE, query: string){
    // possible contact types: consignee_contact_list, shipper_contact_list, carrier_contact_list, customer_contact_list, broker_contact_list, and insurance_contact_list
    let list_type: string = this.CONTACT_TYPE[type];
    let url = `/api/work_orders/contact_autofill.php?type=${list_type}&search=${query}`
    this.http.get<any>(url).subscribe(response => {
      console.log(response.length);
    })
  }

  addPallet(){
    let pallet_form2 = this.fb.group({
      pallet_or_product: ['', Validators.required],
      pallet_qty: [''],
      model_num: [''],
      length: ['', Validators.required],
      width: ['', Validators.required],
      height: ['', Validators.required],
      weight: ['', Validators.required],
      description: ['']
    });
    this.pallet_set.push(pallet_form2);
  }

  removePallet(index: number){
    this.pallet_set.splice(index, 1);
  }

  submitPallets(){
    console.log(this.pallet_set);
    console.log(this.load_form.get('load_details')?.value)
  }

  submit(){
    // call validators, then if error messages exist, show the messages. if no error msgs, call functions to create order
    this.submitPallets();

    /* once work order is created, call operations api and pass info for operations
    need to find a way to get the lading uuid for the order for this to work
    let uuid = '???'
    let ops_url = `/api/work_orders/lading_operations.php?uuid=${uuid}`
    this.http.get<any>(ops_url)
    .subscribe(response => {
      if(response[0]){
        const ops_data = {
          lading_uuid: uuid,
          lading_operation_uuid: response[0].lading_operation_uuid,
          carrier_rated: {ops_form.carrier_rated ? true : null},
          straps_bars: {ops_form.straps_bars ? true : null},
          lift_lull: {ops_form.lift_lull ? true : null},
          call_site: {ops_form.call_site ? true : null},
          protective_equipment: {ops_form.protective_equipment ? true : null}
        }
      }
    })
    if(ops_data){
      this.http.post<any>(ops_url)
      .subscribe(response => {
        console.log(response)
      })
    }
    */
  }

  //create post data for pallet sets
  createPalletSetsPost(){
    let pallet_sets_post: PalletSet[] = [];
    this.pallet_set.forEach((ps) => {
      if(ps.get('pallet_or_product')?.value === 'pallet'){
        let pallet: PalletSet = {
          pallet_num: ps.get('pallet_qty')?.value,
          pallet_length: ps.get('length')?.value,
          pallet_width: ps.get('width')?.value,
          pallet_height: ps.get('height')?.value,
          pallet_weight: ps.get('weight')?.value
        }
        pallet_sets_post.push(pallet);
      }
      else{
        let product: PalletSet = {
          product_model_number: ps.get('model_num')?.value,
          product_length: ps.get('length')?.value,
          product_width: ps.get('width')?.value,
          product_height: ps.get('height')?.value,
          product_weight: ps.get('weight')?.value
        }
        pallet_sets_post.push(product);
      }
    })
  }

  validate_input(){
  }

}

export interface PostData{
  [key: string]: any
}

// pallet/product form
export interface Pallet{
  num_of_pallets?: number,
  length?: number,
  width?: number,
  height?: number,
  weight?: number,
  model_num?: string,
  product_qty?: number,
  description?: string
}

// for pallet/product post data
export interface PalletSet{
  pallet_num?: string, //is it a string??
  pallet_length?: string,
  pallet_width?: string,
  pallet_height?: string,
  pallet_weight?: number,
  product_quantity?: number,
  product_length?: string,
  product_width?: string,
  product_height?: string,
  product_weight?: number,
  product_model_number?: string
}


// validator for pallet form group - checks that appropriate forms are filled depending on item type
export const PalletInfoValidator: ValidatorFn = (control: AbstractControl) => {
  const item = control.get('pallet_or_product')?.value;
  if(item === 'pallet'){
    if(control.get('pallet_qty')?.value === ''){
      return { missingPalletInfo : true};
    }
  }
  else if(item === 'product'){
    if(control.get('model_num')?.value === ''){
      return { missingProductInfo : true};
    }
  }
  return null;
}

// validator for company names
export const CompanyNameValidator: ValidatorFn = (control: AbstractControl) => {
  // to do: check the company name in the database
  return null;
}

// contact objects for autofill feature
export interface ContactAutofill{
  value: string, //contact uuid
  label: string //contact name
}